/**
 * useEditorSqlTokens — SQL tokens coloured exactly like the Query Editor, for
 * read-only previews (the Visual Query Editor's SQL preview), without
 * mounting an editor.
 *
 * Token boundaries come from Monaco's own tokenizer for the language
 * (`monaco.editor.tokenize`) and colours from the same rules the app defines
 * the editor's `vs` / `vs-dark` themes with (`getEditorTokenRules`), matched
 * the way Monaco's theme matches: the longest dot-prefix of the token type
 * (`keyword.sql` → `keyword`). So the preview follows the active editor colour
 * scheme and dark mode. `null` until Monaco has loaded — render plain text.
 *
 * Lossless: joining every segment's `text` gives back the input exactly
 * (line separators are their own uncoloured segments).
 */
import { loader, type Monaco } from '@monaco-editor/react';
import { useEffect, useMemo, useState } from 'react';

import { getEditorTokenRules } from '../../../../core/theme/monacoTheme';
import { useAppStore } from '../../../../store/appStore';
import { buildEditorSqlTokens, type EditorSqlToken } from './editorSqlTokens';

export type { EditorSqlToken } from './editorSqlTokens';

const readyByLanguage = new Map<string, Promise<Monaco>>();

/** Load Monaco and make sure the language's tokenizer is registered (lazy in Monaco). */
function whenTokenizerReady(languageId: string): Promise<Monaco> {
  let ready = readyByLanguage.get(languageId);
  if (!ready) {
    ready = loader
      .init()
      .then(async (monaco) => {
        // `colorize` awaits the language's lazily loaded tokenizer; `tokenize`
        // does not, and would return one uncoloured token per line before it.
        await monaco.editor.colorize('SELECT 1', languageId, { tabSize: 2 });
        return monaco;
      })
      .catch((error: unknown) => {
        readyByLanguage.delete(languageId);
        throw error;
      });
    readyByLanguage.set(languageId, ready);
  }
  return ready;
}

export function useEditorSqlTokens(sql: string, languageId = 'sql'): EditorSqlToken[] | null {
  const [monaco, setMonaco] = useState<Monaco | null>(null);
  const isDark = useAppStore((state) => state.isDarkMode);
  const selection = useAppStore((state) => state.editorColorScheme);
  const customColors = useAppStore((state) => state.customEditorColors);

  useEffect(() => {
    let cancelled = false;
    whenTokenizerReady(languageId)
      .then((m) => {
        if (!cancelled) setMonaco(m);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [languageId]);

  return useMemo(() => {
    if (!monaco || !sql) return null;
    return buildEditorSqlTokens(
      sql,
      (text) => monaco.editor.tokenize(text, languageId),
      getEditorTokenRules(selection, customColors, isDark),
    );
  }, [monaco, sql, languageId, selection, customColors, isDark]);
}
